import React, { useState, useCallback, useEffect } from 'react';
import {
  Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, Button,
  Typography, CircularProgress, Tooltip, IconButton, TextField,
  MenuItem, Grid,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import DeleteSweepOutlinedIcon from '@mui/icons-material/DeleteSweepOutlined';
import { ConfirmationDialog } from '@components/common/ConfirmationDialog.tsx';
import { BaseDialog } from '@components/common/BaseDialog.tsx';
import { PageIndices, TabIndices } from '../../common/i-doc/i-doc.registry.ts';
import { AdaptiveTabs, AdaptiveTabItem, useAdaptiveTab } from '@components/common/AdaptiveTabs.tsx';
import { MET_MISS_REASON_LABELS, WorkoutMetMissesTab } from '@components/admin/ai-cache/WorkoutMetMissesTab.tsx';
import RestartAltOutlinedIcon from '@mui/icons-material/RestartAltOutlined';
import { DashboardLayout } from '@components/layout/DashboardLayout.tsx';
import { PageCard } from '@components/common/PageCard.tsx';
import { PageToolbar } from '@components/common/PageToolbar.tsx';
import { UniversalPagination } from '@components/common/UniversalPagination.tsx';
import { PageHint } from '@components/common/PageHint.tsx';
import { toastService } from '@services/toast.service.ts';
import { adminAiCacheService, AiWorkoutCacheItem } from '@services/admin-ai-cache.service.ts';
import { useDebounce } from '@hooks/useDebounce.ts';

const INTENSITY_LABELS: Record<string, string> = { low: 'Низька', medium: 'Середня', high: 'Висока' };

const CALC_METHOD_LABELS: Record<string, string> = {
  library_match: 'Збіг з бібліотекою тренувань',
  met_db: 'AI вибрав з бази MET',
  ai_estimate: 'Оцінка AI (немає в базі MET)',
};


const WORKOUT_TYPE_LABELS: Record<string, string> = {
  strength: 'Силове',
  cardio: 'Кардіо',
  stretching: 'Розтяжка',
  unrecognized: 'Нерозпізнано',
};

const SOURCE_COLOR: Record<string, 'default' | 'primary' | 'warning'> = {
  ai: 'primary',
  manual: 'warning',
};

// ---------- Detail modal ----------

function WorkoutCacheDetailModal({ item, open, onClose }: {
  item: AiWorkoutCacheItem | null;
  open: boolean;
  onClose: () => void;
}) {
  if (!item) return null;

  const rows = [
    { label: 'ID', value: item.id },
    { label: 'Ключ', value: item.nameKey },
    { label: 'Назва', value: item.displayName },
    { label: 'Тип', value: item.workoutType ? WORKOUT_TYPE_LABELS[item.workoutType] ?? item.workoutType : '—' },
    { label: 'Одиниця', value: item.unit ?? '—' },
    { label: 'Інтенсивність', value: INTENSITY_LABELS[item.intensity] ?? '—' },
    { label: 'Ккал / кг / одиницю', value: item.kcalPerKgPerUnit != null ? item.kcalPerKgPerUnit : '—' },
    { label: 'Хвилин на одиницю', value: item.minutesPerUnit != null ? item.minutesPerUnit : '—' },
    {
      label: 'Калорій / одиниця (довідково)',
      value: item.caloriesPerUnit != null
        ? `${item.caloriesPerUnit} ккал${item.referenceWeightKg != null ? ` для ${item.referenceWeightKg} кг` : ''}`
        : '—',
    },
    { label: 'MET', value: item.metValue != null ? item.metValue : '—' },
    { label: 'Спосіб розрахунку', value: item.calcMethod ? CALC_METHOD_LABELS[item.calcMethod] : '—' },
    { label: 'Чому не з бази MET', value: item.aiEstimateReason ? MET_MISS_REASON_LABELS[item.aiEstimateReason] : '—' },
    { label: 'MET-активність (база)', value: item.metActivityId != null ? `#${item.metActivityId}` : '—' },
    { label: 'Кандидати MET', value: item.metCandidateCodes ?? '—' },
    { label: 'Тренування бібліотеки', value: item.workoutId != null ? `#${item.workoutId}` : '—' },
    { label: 'Схожість збігу', value: item.matchScore != null ? item.matchScore : '—' },
    { label: 'Джерело', value: item.source === 'ai' ? 'AI' : 'Вручну' },
    { label: 'Звернень', value: item.hitCount },
    { label: 'Створено', value: new Date(item.createdAt).toLocaleString('uk') },
    { label: 'Оновлено', value: new Date(item.updatedAt).toLocaleString('uk') },
  ];

  return (
    <BaseDialog open={open} onClose={onClose} maxWidth="sm" title={`Деталі: ${item.displayName}`}>
        <Table size="small">
          <TableBody>
            {rows.map(({ label, value }) => (
              <TableRow key={label}>
                <TableCell sx={{ color: 'text.secondary', width: 160 }}>{label}</TableCell>
                <TableCell>{String(value)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
    </BaseDialog>
  );
}

// ---------- Edit modal ----------

type EditForm = {
  displayName: string;
  workoutType: string;
  unit: string;
  caloriesPerUnit: string;
  metValue: string;
  source: string;
};

function toEditForm(item: AiWorkoutCacheItem): EditForm {
  return {
    displayName: item.displayName,
    workoutType: item.workoutType ?? '',
    unit: item.unit ?? '',
    caloriesPerUnit: item.caloriesPerUnit != null ? String(item.caloriesPerUnit) : '',
    metValue: item.metValue != null ? String(item.metValue) : '',
    source: item.source,
  };
}

function parseNullableFloat(v: string): number | null {
  const n = parseFloat(v);
  return isNaN(n) ? null : n;
}

function WorkoutCacheEditModal({ item, open, onClose, onSaved }: {
  item: AiWorkoutCacheItem | null;
  open: boolean;
  onClose: () => void;
  onSaved: (updated: AiWorkoutCacheItem) => void;
}) {
  const [form, setForm] = useState<EditForm | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (item) setForm(toEditForm(item));
  }, [item]);

  if (!item || !form) return null;

  const set = (field: keyof EditForm) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm((f) => f ? { ...f, [field]: e.target.value } : f);

  const handleSave = async () => {
    if (!form) return;
    setSaving(true);
    try {
      const payload: Partial<AiWorkoutCacheItem> = {
        displayName: form.displayName || undefined,
        workoutType: (form.workoutType as AiWorkoutCacheItem['workoutType']) || null,
        unit: form.unit || null,
        caloriesPerUnit: parseNullableFloat(form.caloriesPerUnit),
        metValue: parseNullableFloat(form.metValue),
        source: form.source as AiWorkoutCacheItem['source'],
      };
      const updated = await adminAiCacheService.updateWorkoutCache(item.id, payload);
      toastService.success('Збережено');
      onSaved(updated);
      onClose();
    } catch (error: any) {
      toastService.error(error?.message || 'Не вдалося зберегти');
    } finally {
      setSaving(false);
    }
  };

  return (
    <BaseDialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      title={`Редагування: ${item.displayName}`}
      actions={
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? 'Збереження…' : 'Зберегти'}
        </Button>
      }
    >
        <Grid container spacing={2} sx={{ pt: 1 }}>
          <Grid item xs={12}>
            <TextField
              label="Назва"
              fullWidth
              value={form.displayName}
              onChange={set('displayName')}
            />
          </Grid>

          <Grid item xs={6}>
            <TextField
              select
              label="Тип тренування"
              fullWidth
              value={form.workoutType}
              onChange={set('workoutType')}
            >
              <MenuItem value="">—</MenuItem>
              <MenuItem value="strength">Силове</MenuItem>
              <MenuItem value="cardio">Кардіо</MenuItem>
              <MenuItem value="stretching">Розтяжка</MenuItem>
              <MenuItem value="unrecognized">Нерозпізнано</MenuItem>
            </TextField>
          </Grid>

          <Grid item xs={6}>
            <TextField
              label="Одиниця"
              fullWidth
              value={form.unit}
              onChange={set('unit')}
              placeholder="хв, км, підх…"
            />
          </Grid>

          <Grid item xs={6}>
            <TextField
              label="Калорій / одиниця"
              fullWidth
              type="number"
              value={form.caloriesPerUnit}
              onChange={set('caloriesPerUnit')}
            />
          </Grid>

          <Grid item xs={6}>
            <TextField
              label="MET"
              fullWidth
              type="number"
              value={form.metValue}
              onChange={set('metValue')}
            />
          </Grid>

          <Grid item xs={6}>
            <TextField
              select
              label="Джерело"
              fullWidth
              value={form.source}
              onChange={set('source')}
            >
              <MenuItem value="ai">AI</MenuItem>
              <MenuItem value="manual">Вручну</MenuItem>
            </TextField>
          </Grid>
        </Grid>
    </BaseDialog>
  );
}

// ---------- Page ----------

const WorkoutCacheTab: React.FC = () => {
  const [items, setItems] = useState<AiWorkoutCacheItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [search, setSearch] = useState('');
  const [source, setSource] = useState('');
  const [workoutType, setWorkoutType] = useState('');
  const [detail, setDetail] = useState<AiWorkoutCacheItem | null>(null);
  const [editing, setEditing] = useState<AiWorkoutCacheItem | null>(null);
  const debouncedSearch = useDebounce(search, 400);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminAiCacheService.getWorkoutCache({
        page, limit,
        search: debouncedSearch || undefined,
        source: source as any || undefined,
        workoutType: workoutType || undefined,
      });
      setItems(res.data);
      setTotal(res.meta.total);
    } catch {
      toastService.error('Не вдалося завантажити кеш тренувань.');
    } finally {
      setLoading(false);
    }
  }, [page, limit, debouncedSearch, source, workoutType]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const handleReset = () => {
    setSearch('');
    setSource('');
    setWorkoutType('');
    setPage(1);
  };

  const [exporting, setExporting] = useState(false);
  const handleExport = async () => {
    setExporting(true);
    try {
      await adminAiCacheService.exportWorkoutCache();
    } catch {
      toastService.error('Не вдалося експортувати кеш тренувань.');
    } finally {
      setExporting(false);
    }
  };

  const [deleting, setDeleting] = useState<AiWorkoutCacheItem | null>(null);
  const handleDeleteItem = async () => {
    const item = deleting;
    setDeleting(null);
    if (!item) return;
    try {
      await adminAiCacheService.deleteWorkoutCacheItem(item.id);
      toastService.success(`Видалено з кешу: ${item.displayName}`);
      setItems(prev => prev.filter(i => i.id !== item.id));
      setTotal(prev => Math.max(0, prev - 1));
    } catch {
      toastService.error('Не вдалося видалити запис.');
    }
  };

  const [confirmClear, setConfirmClear] = useState<'cache' | 'estimates' | null>(null);
  const handleClear = async () => {
    const target = confirmClear;
    setConfirmClear(null);
    try {
      if (target === 'cache') {
        const { deleted, missesDeleted } = await adminAiCacheService.clearWorkoutCache();
        toastService.success(`Очищено: розрахунків - ${deleted}, журнал «не знайдено» - ${missesDeleted}`);
        setPage(1);
        fetchItems();
      } else if (target === 'estimates') {
        const deleted = await adminAiCacheService.clearWorkoutAiEstimates();
        toastService.success(`Прибрано оцінок AI: ${deleted}. Вони перерахуються через базу MET при наступному зверненні`);
        setPage(1);
        fetchItems();
      }
    } catch {
      toastService.error('Не вдалося очистити.');
    }
  };

  const handleSaved = (updated: AiWorkoutCacheItem) => {
    setItems((prev) => prev.map((i) => (i.id === updated.id ? updated : i)));
  };

  return (
    <>
      <PageCard>
        <PageToolbar
          storageKey="filters:admin-ai-workout-cache"
          filters={[
            {
              id: 'search',
              type: 'text',
              label: 'Пошук за назвою',
              value: search,
              onChange: (v) => { setSearch(v); setPage(1); },
              flexGrow: 1,
            },
            {
              id: 'workoutType',
              type: 'select',
              label: 'Тип тренування',
              value: workoutType,
              onChange: (v) => { setWorkoutType(v); setPage(1); },
              options: [
                { value: '', label: 'Всі' },
                { value: 'strength', label: 'Силове' },
                { value: 'cardio', label: 'Кардіо' },
                { value: 'stretching', label: 'Розтяжка' },
                { value: 'unrecognized', label: 'Нерозпізнано' },
              ],
            },
            {
              id: 'source',
              type: 'select',
              label: 'Джерело',
              value: source,
              onChange: (v) => { setSource(v); setPage(1); },
              options: [
                { value: '', label: 'Всі' },
                { value: 'ai', label: 'AI' },
                { value: 'manual', label: 'Вручну' },
              ],
            },
          ]}
          onReset={handleReset}
          extraActions={
            <>
            <Button
              variant="outlined"
              startIcon={exporting ? <CircularProgress size={16} /> : <FileDownloadOutlinedIcon />}
              onClick={handleExport}
              disabled={exporting}
            >
              Експорт (XLSX)
            </Button>
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteSweepOutlinedIcon />}
              onClick={() => setConfirmClear('cache')}
            >
              Очистити кеш
            </Button>
            <Tooltip title="Прибрати з кешу оцінки AI (без MET): наступного разу вони пошукають відповідник у базі MET з поточними ключовими фразами">
              <Button
                variant="outlined"
                color="warning"
                startIcon={<RestartAltOutlinedIcon />}
                onClick={() => setConfirmClear('estimates')}
              >
                Перерахувати нерозпізнані
              </Button>
            </Tooltip>
          </>
          }
          showReset
        />

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : items.length === 0 ? (
          <Typography color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
            Записів не знайдено
          </Typography>
        ) : (
          <TableContainer>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Назва</TableCell>
                  <TableCell>Тип</TableCell>
                  <TableCell>Одиниця</TableCell>
                  <TableCell>Калорій / одиниця</TableCell>
                  <TableCell>MET</TableCell>
                  <TableCell align="center">Звернень</TableCell>
                  <TableCell align="center">Джерело</TableCell>
                  <TableCell align="center" />
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((item) => (
                  <TableRow
                    key={item.id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => setDetail(item)}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>{item.displayName}</Typography>
                      <Typography variant="caption" color="text.secondary">{item.nameKey}</Typography>
                    </TableCell>
                    <TableCell>
                      {item.workoutType ? WORKOUT_TYPE_LABELS[item.workoutType] ?? item.workoutType : '—'}
                    </TableCell>
                    <TableCell>{item.unit ?? '—'}{INTENSITY_LABELS[item.intensity] ? ` · ${INTENSITY_LABELS[item.intensity].toLowerCase()}` : ''}</TableCell>
                    <TableCell>
                      {item.caloriesPerUnit != null ? `${item.caloriesPerUnit} ккал` : '—'}
                    </TableCell>
                    <TableCell>
                      {item.metValue != null ? item.metValue : '—'}
                    </TableCell>
                    <TableCell align="center">
                      <Chip label={item.hitCount} size="small" color={item.hitCount > 0 ? 'success' : 'default'} variant="outlined" />
                    </TableCell>
                    <TableCell align="center">
                      <Chip label={item.source === 'ai' ? 'AI' : 'Вручну'} size="small" color={SOURCE_COLOR[item.source]} />
                    </TableCell>
                    <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                      <Tooltip title="Детальніше">
                        <IconButton size="small" onClick={() => setDetail(item)}>
                          <InfoOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Редагувати">
                        <IconButton size="small" onClick={() => setEditing(item)}>
                          <EditOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Видалити з кешу">
                        <IconButton size="small" onClick={() => setDeleting(item)}>
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <UniversalPagination
          page={page}
          count={Math.ceil(total / limit)}
          limit={limit}
          total={total}
          onPageChange={setPage}
          onLimitChange={(v) => { setLimit(v); setPage(1); }}
          storageKey="pagination:admin-ai-workout-cache"
        />
      </PageCard>

      <WorkoutCacheDetailModal
        item={detail}
        open={!!detail}
        onClose={() => setDetail(null)}
      />

      <WorkoutCacheEditModal
        item={editing}
        open={!!editing}
        onClose={() => setEditing(null)}
        onSaved={handleSaved}
      />

      <ConfirmationDialog
        open={deleting !== null}
        onClose={() => setDeleting(null)}
        onConfirm={handleDeleteItem}
        title="Видалити запис з кешу?"
        message={deleting
          ? `«${deleting.displayName}» (${deleting.unit ?? '—'}${INTENSITY_LABELS[deleting.intensity] ? `, ${INTENSITY_LABELS[deleting.intensity].toLowerCase()}` : ''}) буде видалено з кешу розрахунків. Наступного разу ця активність порахується заново через базу MET.`
          : ''}
        confirmText="Видалити"
        cancelText="Скасувати"
      />

      <ConfirmationDialog
        open={confirmClear !== null}
        onClose={() => setConfirmClear(null)}
        onConfirm={handleClear}
        title={confirmClear === 'estimates' ? 'Перерахувати нерозпізнані?' : 'Очистити кеш розрахунків тренувань?'}
        message={confirmClear === 'estimates'
          ? 'З кешу буде прибрано всі оцінки AI (активності без відповідника в базі MET). При наступному зверненні кожна з них знову пошукає відповідник у базі MET (більше запитів до AI). Розрахунки з бази MET і журнал «не знайдено» лишаються. Це відбувається й автоматично після зміни ключових фраз або перебудови індексу MET.'
          : 'Буде видалено всі розрахунки калорій тренувань разом із журналом «не знайдено в MET» - накопичення почнеться з нуля; кожна активність перерахується при першому зверненні (більше запитів до AI). Кеш розпізнавання тексту не зачіпається. Якщо потрібні поточні дані - спершу зробіть «Експорт (XLSX)». Дію не можна скасувати.'}
        confirmText={confirmClear === 'estimates' ? 'Перерахувати' : 'Очистити'}
        cancelText="Скасувати"
      />
    </>
  );
};

const TABS_STORAGE_KEY = 'tab:admin-ai-workout-cache';

const TABS: AdaptiveTabItem[] = [
  { value: 'cache', label: 'Кеш розрахунків', iDoc: TabIndices.ADMIN_AI_WORKOUT_CACHE },
  { value: 'met-misses', label: 'Не знайдено в MET', iDoc: TabIndices.ADMIN_AI_WORKOUT_MET_MISSES },
];

const AdminAiWorkoutCachePageContent: React.FC = () => {
  const currentTab = useAdaptiveTab(TABS, { searchParamKey: 'tab', defaultValue: 'cache', storageKey: TABS_STORAGE_KEY });

  return (
    <Box data-i-doc={PageIndices.ADMIN_AI_WORKOUT}>
      <PageHint iDoc={PageIndices.ADMIN_AI_WORKOUT} />
      <AdaptiveTabs tabs={TABS} searchParamKey="tab" defaultValue="cache" storageKey={TABS_STORAGE_KEY} />
      {currentTab === 'cache' && (
        <Box data-i-doc={TabIndices.ADMIN_AI_WORKOUT_CACHE}>
          <PageHint iDoc={TabIndices.ADMIN_AI_WORKOUT_CACHE} />
          <WorkoutCacheTab />
        </Box>
      )}
      {currentTab === 'met-misses' && (
        <Box data-i-doc={TabIndices.ADMIN_AI_WORKOUT_MET_MISSES}>
          <PageHint iDoc={TabIndices.ADMIN_AI_WORKOUT_MET_MISSES} />
          <WorkoutMetMissesTab />
        </Box>
      )}
    </Box>
  );
};

export const AdminAiWorkoutCachePage: React.FC = () => (
  <DashboardLayout>
    <AdminAiWorkoutCachePageContent />
  </DashboardLayout>
);
